/**
 * Which settings UI a widget type gets: the five bespoke core editors (ticket list, counter, links, notes, report), or a `SchemaForm` over the
 * definition's `settingsSchema` (every other core widget and every plugin widget).
 */
import type { ComponentType } from "react";
import type { JsonSchema } from "@/components/fields/jsonSchema";
import type { WidgetDefinition } from "@/plugins/dashboardWidgets";
import { CounterSettingsEditor } from "./editors/CounterSettings";
import { LinksSettingsEditor } from "./editors/LinksSettings";
import { NotesSettingsEditor } from "./editors/NotesSettings";
import { ReportSettingsEditor } from "./editors/ReportSettings";
import { TicketListSettingsEditor } from "./editors/TicketListSettings";

export interface SettingsEditorProps {
  value: Record<string, unknown>;
  onChange(next: Record<string, unknown>): void;
  /** Field errors keyed by the field path inside the settings (`limit`, `items.0.url`). */
  errors: Record<string, string>;
  /** Focus the source section on mount (opened from a tile's "Suche wählen"). */
  focusSource?: boolean;
}

export const CORE_SETTINGS_EDITORS: Record<string, ComponentType<SettingsEditorProps>> = {
  "core.ticket-list": TicketListSettingsEditor,
  "core.counter": CounterSettingsEditor,
  "core.links": LinksSettingsEditor,
  "core.notes": NotesSettingsEditor,
  "core.report": ReportSettingsEditor,
};

const isSourcePath = (p: string) => p === "source" || p.startsWith("source.");

/** The server error paths (inside `settings`) each bespoke editor shows next to a field. */
const CORE_EDITOR_ERROR_PATHS: Record<string, (path: string) => boolean> = {
  "core.ticket-list": (p) => isSourcePath(p) || ["columns", "sort", "order", "limit"].includes(p),
  "core.counter": isSourcePath,
  "core.links": (p) => /^items\.\d+\.(label|url)$/.test(p),
  "core.notes": (p) => p === "markdown",
  "core.report": (p) => /^(report_key|time_range|display|max_rows|parameters(\..+)?|dimension_choices(\..+)?)$/.test(p),
};

/**
 * Whether the settings UI of `def` shows a server error at `path` (`limit`, `items.0.url`) next to
 * a field. Anything else is shown as a general message in the drawer, so no 422 text gets lost.
 */
export function showsErrorPath(def: Pick<WidgetDefinition, "key">, adapter: FormAdapter | null, path: string): boolean {
  const bespoke = CORE_EDITOR_ERROR_PATHS[def.key];
  if (bespoke) return bespoke(path);
  return adapter !== null && Object.prototype.hasOwnProperty.call(adapter.schema.properties ?? {}, path);
}

/** A generated form over (a view of) the stored settings. */
export interface FormAdapter {
  schema: JsonSchema;
  /** Prefix of the field texts (`<prefix>.fields.<key>.label|help|options.<value>`). */
  i18nPrefix: string;
  toForm(settings: Record<string, unknown>): Record<string, unknown>;
  fromForm(form: Record<string, unknown>, prev: Record<string, unknown>): Record<string, unknown>;
}

const CORE_FORM_NAMES: Record<string, string> = {
  "core.queue-overview": "queueOverview",
  "core.escalations": "escalations",
  "core.reminders": "reminders",
  "core.recent-views": "recentViews",
  "core.calendar-agenda": "calendarAgenda",
  "core.team-presence": "teamPresence",
  "core.sla-gauge": "slaGauge",
  "core.backlog-aging": "backlogAging",
  "core.heatmap": "heatmap",
  "core.chat-queue": "chatQueue",
};

const identity = {
  toForm: (s: Record<string, unknown>) => s,
  fromForm: (f: Record<string, unknown>) => f,
};

/** The generated form of a widget type, or null when it has no settings schema (title only). */
export function formAdapterFor(def: Pick<WidgetDefinition, "key" | "owner" | "ns" | "settingsSchema">): FormAdapter | null {
  if (!def.settingsSchema) return null;
  const schema = def.settingsSchema as JsonSchema;
  if (def.owner === "core") {
    const name = CORE_FORM_NAMES[def.key];
    return name ? { ...identity, schema, i18nPrefix: `dashboards.settings.forms.${name}` } : null;
  }
  // Plugin widgets: labels come from the plugin's own namespace (`<ns>:widget.<key>.fields.<field>.label`),
  // falling back to the schema's titles.
  const local = def.key.startsWith(`${def.owner}.`) ? def.key.slice(def.owner.length + 1) : def.key;
  return { ...identity, schema, i18nPrefix: `${def.ns ?? `plugin.${def.owner}`}:widget.${local}` };
}
